import { useCallback, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";

export type UserDisplayNameInput = {
  uid?: string;
  id?: string;
  _id?: string;
  userId?: string;

  displayName?: string;
  name?: string;
  fullName?: string;
  username?: string;
  email?: string;

  firstName?: string;
  lastName?: string;

  businessName?: string;
  shopName?: string;
  companyName?: string;
};

export type UserDisplayNameMap = Record<string, UserDisplayNameInput>;

const cleanString = (value: unknown) => String(value || "").trim();

export const getUserId = (user: unknown): string => {
  if (!user || typeof user !== "object") {
    return cleanString(user);
  }

  const item = user as UserDisplayNameInput;

  return cleanString(item.uid || item.userId || item.id || item._id);
};

export const getDisplayNameFromUser = (
  user: UserDisplayNameInput | null | undefined,
): string => {
  if (!user) return "";

  const displayName = cleanString(user.displayName);
  if (displayName) return displayName;

  const name = cleanString(user.name);
  if (name) return name;

  const fullName = cleanString(user.fullName);
  if (fullName) return fullName;

  const firstName = cleanString(user.firstName);
  const lastName = cleanString(user.lastName);
  const joinedName = cleanString(`${firstName} ${lastName}`);
  if (joinedName) return joinedName;

  const businessName = cleanString(user.businessName);
  if (businessName) return businessName;

  const shopName = cleanString(user.shopName);
  if (shopName) return shopName;

  const companyName = cleanString(user.companyName);
  if (companyName) return companyName;

  const username = cleanString(user.username);
  if (username) return username;

  const email = cleanString(user.email);
  if (email) return email;

  return "";
};

export const buildUserDisplayNameMap = (
  users: UserDisplayNameInput[] = [],
): UserDisplayNameMap => {
  return users.reduce<UserDisplayNameMap>((acc, user) => {
    const id = getUserId(user);

    if (id) {
      acc[id] = user;
    }

    return acc;
  }, {});
};

export const getUserDisplayName = (
  userOrId: unknown,
  usersMap?: UserDisplayNameMap | null,
): string => {
  const fallbackId = getUserId(userOrId);

  if (userOrId && typeof userOrId === "object") {
    const displayName = getDisplayNameFromUser(
      userOrId as UserDisplayNameInput,
    );

    return displayName || fallbackId;
  }

  if (!fallbackId) return "";

  if (!usersMap) return fallbackId;

  const user = usersMap[fallbackId];

  if (!user) return fallbackId;

  return getDisplayNameFromUser(user) || fallbackId;
};

/**
 * This extracts users from AuthContext without forcing one exact property name.
 * It supports common AuthContext shapes like:
 *
 * users
 * allUsers
 * members
 * sellers
 * userSettings.users
 * userSettings.members
 */
const getUsersFromAuthContext = (auth: any): UserDisplayNameInput[] => {
  const candidates = [
    auth?.users,
    auth?.allUsers,
    auth?.members,
    auth?.sellers,
    auth?.userSettings?.users,
    auth?.userSettings?.members,
    auth?.userSettings?.sellers,
  ];

  const found = candidates.find((value) => Array.isArray(value));

  return Array.isArray(found) ? found : [];
};

export const useUserDisplayNamesFromAuth = () => {
  const auth = useAuth() as any;

  const users = useMemo(() => getUsersFromAuthContext(auth), [auth]);

  const usersMap = useMemo(() => buildUserDisplayNameMap(users), [users]);

  const getDisplayName = useCallback(
    (userOrId: unknown) => getUserDisplayName(userOrId, usersMap),
    [usersMap],
  );

  return useMemo(
    () => ({
      users,
      usersMap,
      getDisplayName,
      loading:
        Boolean(auth?.usersLoading) ||
        Boolean(auth?.membersLoading) ||
        Boolean(auth?.sellersLoading),
    }),
    [
      auth?.membersLoading,
      auth?.sellersLoading,
      auth?.usersLoading,
      getDisplayName,
      users,
      usersMap,
    ],
  );
};
